import { fetchAPIFrom, headerJson, headerFormData } from './common';
import { backendURL } from 'config';

const p = (uri, data, headers, method) =>
  new Promise((resolve, reject) =>
    fetchAPIFrom(uri, data, { cache: 'no-cache', headers }, method)
      .then(resolve)
      .catch(reject)
  );

// Posts JSON to a `text/csv` endpoint and triggers a browser download.
// Used by the platform-admin export buttons — bypasses `p()` (which
// assumes JSON) and pulls the filename out of the Content-Disposition
// header set by the server.
const adminCsvFetch = (uri, data) =>
  fetch(`${backendURL}/${uri}`, {
    method: 'POST',
    headers: headerJson(),
    body: JSON.stringify(data || {}),
  }).then(async (res) => {
    if (!res.ok) {
      let message = `Export failed (${res.status})`;
      try {
        const body = await res.json();
        if (body?.message) message = body.message;
      } catch (_) { /* server didn't return JSON */ }
      throw new Error(message);
    }
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = match ? match[1] : 'export.csv';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  });

export const API = {

  // ─── Auth ─────────────────────────────────────────────────────────────────────
  signUp: (data, referral) => p(`auth/signup/${referral}`, data, { 'Content-Type': 'application/json' }, 'post'),
  signIn: (data) => p('auth/signin', data, { 'Content-Type': 'application/json' }, 'post'),
  signOut: () => p('auth/signout', null, headerJson(), 'post'),
  verificationMail: (data) => p('auth/confirm', data, { 'Content-Type': 'application/json' }, 'post'),
  resendVerificationMail: (data) => p('auth/sendVerifyMail', data, { 'Content-Type': 'application/json' }, 'post'),
  sendResetPassword: (data) => p('auth/sendresetPassword', data, { 'Content-Type': 'application/json' }, 'post'),
  resetPassword: (token, data) => p(`auth/resetPassword/${token}`, data, { 'Content-Type': 'application/json' }, 'post'),
  changePassword: (data) => p('auth/changePassword', data, headerJson(), 'post'),
  deactivateUser: (data) => p('auth/deactiveUser', data, headerJson(), 'post'),
  deleteAccount: (data) => p('auth/deleteAccount', data, headerJson(), 'post'),
  sendReactive: (data) => p('auth/sendReactive', data, { 'Content-Type': 'application/json' }, 'post'),
  reactiveUser: (code) => p(`auth/reactiveUser/${code}`, null, { 'Content-Type': 'application/json' }, 'post'),

  // ─── User Profile ─────────────────────────────────────────────────────────────
  // NOTE: for GET requests fetchAPIFrom passes 2nd arg as axios config, so headers go there
  getUserProfile: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('user/profile/findByUserId', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  updateUserProfile: (data) => p('user/profile/saveAndUpdate', data, headerFormData(), 'post'),
  changeUsername: (data) => p('user/change-username', data, headerJson(), 'post'),
  deleteUserProfileLogo: () => p('user/profile/deleteLogoByUserId', null, headerJson(), 'delete'),

  // ─── Company / Brand Profile ──────────────────────────────────────────────────
  getBrandById: (data) => p('dashboard/brand/profile/findBrandById', data, headerJson(), 'post'),
  getBrandByDomain: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('dashboard/brand/profile/findBrandByDomain', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  submitBrandData: (data) => p('dashboard/brand/profile/createBrand', data, headerJson(), 'post'),
  updateBrandData: (data) => p('dashboard/brand/profile/update', data, headerFormData(), 'post'),
  deactivateBrand: (data) => p('dashboard/brand/profile/deactive', data, headerJson(), 'post'),
  deleteBrandLogo: () => p('dashboard/brand/profile/deleteLogoByUserId', null, headerJson(), 'delete'),

  // ─── Custom domains (DNS white-label) ─────────────────────────────────────────
  listBrandDomains: (data) => p('dashboard/brand/domains/list', data, headerJson(), 'post'),
  addBrandDomain: (data) => p('dashboard/brand/domains/add', data, headerJson(), 'post'),
  deleteBrandDomain: (data) => p('dashboard/brand/domains/delete', data, headerJson(), 'post'),
  verifyBrandDomain: (data) => p('dashboard/brand/domains/verify', data, headerJson(), 'post'),

  // ─── Subdomain routing under the brand's primary domain ──────────────────────
  listBrandSubdomains: (data) => p('dashboard/brand/subdomains/list', data, headerJson(), 'post'),
  addBrandSubdomain: (data) => p('dashboard/brand/subdomains/add', data, headerJson(), 'post'),
  updateBrandSubdomain: (data) => p('dashboard/brand/subdomains/update', data, headerJson(), 'post'),
  deleteBrandSubdomain: (data) => p('dashboard/brand/subdomains/delete', data, headerJson(), 'post'),

  // ─── Brand Mail (per-brand SendGrid + templates) ──────────────────────────────
  getBrandMailConfig: (data) => p('dashboard/brand/mail/config/get', data, headerJson(), 'post'),
  upsertBrandMailConfig: (data) => p('dashboard/brand/mail/config/upsert', data, headerJson(), 'post'),
  listBrandMailTemplates: (data) => p('dashboard/brand/mail/templates/list', data, headerJson(), 'post'),
  getBrandMailTemplate: (data) => p('dashboard/brand/mail/templates/get', data, headerJson(), 'post'),
  upsertBrandMailTemplate: (data) => p('dashboard/brand/mail/templates/upsert', data, headerJson(), 'post'),
  deleteBrandMailTemplate: (data) => p('dashboard/brand/mail/templates/delete', data, headerJson(), 'post'),
  testSendBrandMail: (data) => p('dashboard/brand/mail/test-send', data, headerJson(), 'post'),

  // ─── NFT Studio (brand-scoped collections) ────────────────────────────────────
  getNftCatalog: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('dashboard/brand/nft/catalog', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  listNftCollections: (data) => p('dashboard/brand/nft/collections/list', data, headerJson(), 'post'),
  getNftCollection: (data) => p('dashboard/brand/nft/collections/get', data, headerJson(), 'post'),
  createNftCollection: (data) => p('dashboard/brand/nft/collections/create', data, headerJson(), 'post'),
  updateNftCollection: (data) => p('dashboard/brand/nft/collections/update', data, headerJson(), 'post'),
  deleteNftCollection: (data) => p('dashboard/brand/nft/collections/delete', data, headerJson(), 'post'),
  deployNftCollection: (data) => p('dashboard/brand/nft/collections/deploy', data, headerJson(), 'post'),

  // ─── Ecommerce: warehouses + products + inventory ─────────────────────────────
  listShopWarehouses: (data) => p('dashboard/brand/shop/warehouses/list', data, headerJson(), 'post'),
  createShopWarehouse: (data) => p('dashboard/brand/shop/warehouses/create', data, headerJson(), 'post'),
  updateShopWarehouse: (data) => p('dashboard/brand/shop/warehouses/update', data, headerJson(), 'post'),
  deleteShopWarehouse: (data) => p('dashboard/brand/shop/warehouses/delete', data, headerJson(), 'post'),

  listShopProducts: (data) => p('dashboard/brand/shop/products/list', data, headerJson(), 'post'),
  importShopProductsCsv: (formData) => p('dashboard/brand/shop/products/import-csv', formData, headerFormData(), 'post'),
  getShopProduct: (data) => p('dashboard/brand/shop/products/get', data, headerJson(), 'post'),
  createShopProduct: (data) => p('dashboard/brand/shop/products/create', data, headerJson(), 'post'),
  updateShopProduct: (data) => p('dashboard/brand/shop/products/update', data, headerJson(), 'post'),
  deleteShopProduct: (data) => p('dashboard/brand/shop/products/delete', data, headerJson(), 'post'),
  addShopVariant: (data) => p('dashboard/brand/shop/variants/add', data, headerJson(), 'post'),
  updateShopVariant: (data) => p('dashboard/brand/shop/variants/update', data, headerJson(), 'post'),
  deleteShopVariant: (data) => p('dashboard/brand/shop/variants/delete', data, headerJson(), 'post'),

  getShopStockMatrix: (data) => p('dashboard/brand/shop/inventory/matrix', data, headerJson(), 'post'),
  listShopInventoryMovements: (data) => p('dashboard/brand/shop/inventory/movements', data, headerJson(), 'post'),
  applyShopInventoryMovement: (data) => p('dashboard/brand/shop/inventory/apply', data, headerJson(), 'post'),
  getShopLowStock: (data) => p('dashboard/brand/shop/inventory/low-stock', data, headerJson(), 'post'),
  bulkSetShopThresholds: (data) => p('dashboard/brand/shop/inventory/thresholds', data, headerJson(), 'post'),
  transferShopStock: (data) => p('dashboard/brand/shop/inventory/transfer', data, headerJson(), 'post'),
  pickShopFulfillment: (data) => p('dashboard/brand/shop/fulfillment/pick', data, headerJson(), 'post'),

  // ─── Product Reviews (owner moderation) ─────────────────────────────────────
  listProductReviews: (data) => p('dashboard/brand/shop/reviews/list', data, headerJson(), 'post'),
  moderateProductReview: (data) => p('dashboard/brand/shop/reviews/moderate', data, headerJson(), 'post'),
  deleteProductReview: (data) => p('dashboard/brand/shop/reviews/delete', data, headerJson(), 'post'),

  // ─── Accounting: tax rules + invoices + reports ───────────────────────────────
  listTaxRules: (data) => p('dashboard/brand/accounting/tax-rules/list', data, headerJson(), 'post'),
  createTaxRule: (data) => p('dashboard/brand/accounting/tax-rules/create', data, headerJson(), 'post'),
  updateTaxRule: (data) => p('dashboard/brand/accounting/tax-rules/update', data, headerJson(), 'post'),
  deleteTaxRule: (data) => p('dashboard/brand/accounting/tax-rules/delete', data, headerJson(), 'post'),
  previewTaxes: (data) => p('dashboard/brand/accounting/tax-rules/preview', data, headerJson(), 'post'),
  getPlatformTaxTable: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('dashboard/brand/accounting/tax-rules/platform-table', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),

  listInvoices: (data) => p('dashboard/brand/accounting/invoices/list', data, headerJson(), 'post'),
  getInvoice: (data) => p('dashboard/brand/accounting/invoices/get', data, headerJson(), 'post'),
  createInvoice: (data) => p('dashboard/brand/accounting/invoices/create', data, headerJson(), 'post'),
  updateInvoice: (data) => p('dashboard/brand/accounting/invoices/update', data, headerJson(), 'post'),
  deleteInvoice: (data) => p('dashboard/brand/accounting/invoices/delete', data, headerJson(), 'post'),
  issueInvoice: (data) => p('dashboard/brand/accounting/invoices/issue', data, headerJson(), 'post'),
  markInvoicePaid: (data) => p('dashboard/brand/accounting/invoices/mark-paid', data, headerJson(), 'post'),
  voidInvoice: (data) => p('dashboard/brand/accounting/invoices/void', data, headerJson(), 'post'),

  getTaxReport: (data) => p('dashboard/brand/accounting/reports/tax', data, headerJson(), 'post'),

  // ─── Brand customer-facing storefront (per-brand customer login) ──────────────
  // Owner-side
  listBrandCustomers: (data) => p('dashboard/brand/customers/list', data, headerJson(), 'post'),
  broadcastBrandCustomers: (data) => p('dashboard/brand/customers/broadcast', data, headerJson(), 'post'),
  getBrandCustomer: (data) => p('dashboard/brand/customers/get', data, headerJson(), 'post'),
  updateBrandCustomer: (data) => p('dashboard/brand/customers/update', data, headerJson(), 'post'),

  // ─── Brand Analytics ──────────────────────────────────────────────────────────
  getBrandOverview: (data) => p('dashboard/brand/analytics/overview', data, headerJson(), 'post'),

  // ─── Brand Orders ─────────────────────────────────────────────────────────────
  listBrandOrders: (data) => p('dashboard/brand/orders/list', data, headerJson(), 'post'),
  listBrandOrdersPage: (data) => p('dashboard/brand/orders/list-page', data, headerJson(), 'post'),
  getBrandOrder: (data) => p('dashboard/brand/orders/get', data, headerJson(), 'post'),
  createBrandOrder: (data) => p('dashboard/brand/orders/create', data, headerJson(), 'post'),
  updateBrandOrder: (data) => p('dashboard/brand/orders/update', data, headerJson(), 'post'),
  markBrandOrderPaid: (data) => p('dashboard/brand/orders/mark-paid', data, headerJson(), 'post'),
  fulfillBrandOrder: (data) => p('dashboard/brand/orders/fulfill', data, headerJson(), 'post'),
  cancelBrandOrder: (data) => p('dashboard/brand/orders/cancel', data, headerJson(), 'post'),
  refundBrandOrder: (data) => p('dashboard/brand/orders/refund', data, headerJson(), 'post'),
  bulkBrandOrders: (data) => p('dashboard/brand/orders/bulk', data, headerJson(), 'post'),

  // ─── Brand Discount Codes ────────────────────────────────────────────────────
  listBrandDiscounts: (data) => p('dashboard/brand/discounts/list', data, headerJson(), 'post'),
  getBrandDiscount: (data) => p('dashboard/brand/discounts/get', data, headerJson(), 'post'),
  createBrandDiscount: (data) => p('dashboard/brand/discounts/create', data, headerJson(), 'post'),
  updateBrandDiscount: (data) => p('dashboard/brand/discounts/update', data, headerJson(), 'post'),
  deleteBrandDiscount: (data) => p('dashboard/brand/discounts/delete', data, headerJson(), 'post'),
  brandDiscountUsage: (data) => p('dashboard/brand/discounts/usage', data, headerJson(), 'post'),

  // ─── Brand Payments (per-brand Stripe via the vault) ──────────────────────────
  getBrandPaymentConfig: (data) => p('dashboard/brand/payments/config/get', data, headerJson(), 'post'),
  upsertBrandPaymentConfig: (data) => p('dashboard/brand/payments/config/upsert', data, headerJson(), 'post'),

  // ─── Company Access / IAM ─────────────────────────────────────────────────────
  getBrandsFromAccessListbyUserId: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('dashboard/settings/iam/getBrandsFromAccessListByUserId', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  addUserToAccessList: (data) => p('dashboard/settings/iam/addUserToAccessList', data, headerJson(), 'post'),
  getAccessList: (data) => p('dashboard/settings/iam/getAccessList', data, headerJson(), 'post'),
  removeUserFromAccessList: (data) => p('dashboard/settings/iam/removeUserFromAccessList', data, headerJson(), 'post'),
  updateUserRole: (data) => p('dashboard/settings/iam/updateUserRole', data, headerJson(), 'post'),
  getRoles: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('dashboard/settings/iam/getRoles', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  listIamInvites: (data) => p('dashboard/settings/iam/invites/list', data, headerJson(), 'post'),
  revokeIamInvite: (data) => p('dashboard/settings/iam/invites/revoke', data, headerJson(), 'post'),
  acceptIamInvite: (data) => p('dashboard/settings/iam/invites/accept', data, headerJson(), 'post'),
  // ─── Notifications (per-brand, per-user) ─────────────────────────────────
  listNotifications: (data) => p('dashboard/notifications/list', data, headerJson(), 'post'),
  notificationsUnreadCount: (data) => p('dashboard/notifications/unread-count', data, headerJson(), 'post'),
  markNotificationRead: (data) => p('dashboard/notifications/mark-read', data, headerJson(), 'post'),
  markAllNotificationsRead: (data) => p('dashboard/notifications/mark-all-read', data, headerJson(), 'post'),
  listBrandActivity: (data) => p('dashboard/activity/list', data, headerJson(), 'post'),

  // ─── Brand API keys ─────────────────────────────────────────────────────────
  listBrandApiKeys: (data) => p('dashboard/api-keys/list', data, headerJson(), 'post'),
  createBrandApiKey: (data) => p('dashboard/api-keys/create', data, headerJson(), 'post'),
  revokeBrandApiKey: (data) => p('dashboard/api-keys/revoke', data, headerJson(), 'post'),
  deleteBrandApiKey: (data) => p('dashboard/api-keys/delete', data, headerJson(), 'post'),

  // ─── Brand Webhooks (outbound) ───────────────────────────────────────────────
  listBrandWebhooks: (data) => p('dashboard/brand/webhooks/list', data, headerJson(), 'post'),
  createBrandWebhook: (data) => p('dashboard/brand/webhooks/create', data, headerJson(), 'post'),
  updateBrandWebhook: (data) => p('dashboard/brand/webhooks/update', data, headerJson(), 'post'),
  deleteBrandWebhook: (data) => p('dashboard/brand/webhooks/delete', data, headerJson(), 'post'),
  rotateBrandWebhookSecret: (data) => p('dashboard/brand/webhooks/rotate-secret', data, headerJson(), 'post'),
  listBrandWebhookDeliveries: (data) => p('dashboard/brand/webhooks/deliveries', data, headerJson(), 'post'),

  // ─── Contact Inbox (owner) ──────────────────────────────────────────────────
  listBrandContact: (data) => p('dashboard/brand/contact/list', data, headerJson(), 'post'),
  setBrandContactStatus: (data) => p('dashboard/brand/contact/set-status', data, headerJson(), 'post'),
  deleteBrandContact: (data) => p('dashboard/brand/contact/delete', data, headerJson(), 'post'),

  // Public — used by the accept-invite landing page before sign-in.
  getIamInviteByToken: (token) =>
    new Promise((resolve, reject) =>
      fetchAPIFrom(
        `public/iam/invites/get?token=${encodeURIComponent(token || '')}`,
        { cache: 'no-cache' },
        null,
        'get'
      ).then(resolve).catch(reject)
    ),

  // ─── Page Builder / Stages ────────────────────────────────────────────────────
  insertUpdateItemToStage: (data) => p('dashboard/brand/pages/builder/saveAndUpdate', data, headerFormData(), 'post'),
  deleteItemFromStage: (data) => p('dashboard/brand/pages/builder/deleteById', data, headerJson(), 'post'),
  getStageById: (data) => p('dashboard/brand/pages/builder/findById', data, headerJson(), 'post'),
  getStageByBrandId: (data) => p('dashboard/brand/pages/builder/getStageByBrandId', data, headerJson(), 'post'),
  getAllStageByBrandId: (data) => p('dashboard/brand/pages/builder/getAllByBrandId', data, headerJson(), 'post'),
  getAllByDomain: (data) => p('dashboard/brand/pages/builder/getAllByDomain', data, { 'Content-Type': 'application/json' }, 'post'),
  getStageFieldsBytype: (data) => p('dashboard/brand/pages/builder/getStageFieldsBytype', data, headerJson(), 'post'),
  getStageDetailsById: (data) => p('dashboard/brand/pages/builder/findById', data, headerJson(), 'post'),

  // What the storefront login page may know: which buttons work, and whose
  // they are. No client ids — see the controller.
  publicAuthProviders: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('public/auth/providers', { cache: 'no-cache' }, null, 'get')
        .then(resolve).catch(reject)
    ),

  // ─── Customer sign-in configuration ──────────────────────────────────────────
  getBrandAuthConfig: (data) => p('dashboard/brand/auth-config', data, headerJson(), 'post'),
  updateBrandAuthConfig: (data) => p('dashboard/brand/auth-config/update', data, headerJson(), 'post'),

  // ─── Subscription ────────────────────────────────────────────────────────────
  getBrandSubscription: (data) => p('dashboard/brand/subscription', data, headerJson(), 'post'),
  listSubscriptionPlans: (data) => p('dashboard/brand/subscription/plans', data, headerJson(), 'post'),
  startSubscriptionCheckout: (data) => p('dashboard/brand/subscription/checkout', data, headerJson(), 'post'),
  openBillingPortal: (data) => p('dashboard/brand/subscription/portal', data, headerJson(), 'post'),

  // ─── Bookings ────────────────────────────────────────────────────────────────
  listBookableResources: (data) => p('dashboard/brand/bookings/resources/list', data, headerJson(), 'post'),
  createBookableResource: (data) => p('dashboard/brand/bookings/resources/create', data, headerJson(), 'post'),
  updateBookableResource: (data) => p('dashboard/brand/bookings/resources/update', data, headerJson(), 'post'),
  deleteBookableResource: (data) => p('dashboard/brand/bookings/resources/delete', data, headerJson(), 'post'),
  listReservations: (data) => p('dashboard/brand/bookings/list', data, headerJson(), 'post'),
  setReservationStatus: (data) => p('dashboard/brand/bookings/status', data, headerJson(), 'post'),
  createReservation: (data) => p('dashboard/brand/bookings/create', data, headerJson(), 'post'),

  // Public booking — the brand comes from the Host header, so no identifier.
  publicBookableResources: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('public/bookings/resources', { cache: 'no-cache' }, null, 'get')
        .then(resolve).catch(reject)
    ),
  publicBookingDay: ({ resource_id, date }) =>
    new Promise((resolve, reject) => {
      const qs = new URLSearchParams();
      if (resource_id) qs.set('resource_id', resource_id);
      if (date) qs.set('date', date);
      fetchAPIFrom(`public/bookings/day?${qs.toString()}`, { cache: 'no-cache' }, null, 'get')
        .then(resolve).catch(reject);
    }),
  publicBook: (data) => p('public/bookings/book', data, headerJson(), 'post'),
  publicCancelBooking: (data) => p('public/bookings/cancel', data, headerJson(), 'post'),

  // ─── Site Builder (Page / Section / Component, brand-scoped) ──────────────────
  getSiteCatalog: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('dashboard/brand/site/catalog', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  listSectionPresets: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('dashboard/brand/site/section-presets', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  listSiteTemplates: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('dashboard/brand/site/templates', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  // Platform-wide list of titles already in use, plus a few defaults. Feeds
  // the free-text dropdown in the create-page dialog.
  listSitePageTitles: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('dashboard/brand/site/page-titles', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  // What the brand has filled in across the content library, with counts.
  getLibrarySummary: (data) => p('dashboard/brand/site/library/summary', data, headerJson(), 'post'),
  getContactAddresses: (data) => p('dashboard/brand/site/contact-addresses', data, headerJson(), 'post'),
  listEnquiries: (data) => p('dashboard/brand/site/enquiries', data, headerJson(), 'post'),
  // Brand-level header/footer used by every page.
  getSiteGlobal: (data) => p('dashboard/brand/site/global/get', data, headerJson(), 'post'),
  updateSiteGlobal: (data) => p('dashboard/brand/site/global/update', data, headerJson(), 'post'),
  publishSiteGlobal: (data) => p('dashboard/brand/site/global/publish', data, headerJson(), 'post'),
  // Build with AI. `status` says whether the server has a key configured, so
  // the button can stay hidden rather than failing after someone has typed.
  aiSiteStatus: (data) => p('dashboard/brand/site/ai/status', data, headerJson(), 'post'),
  aiGenerateSite: (data) => p('dashboard/brand/site/ai/generate', data, headerJson(), 'post'),
  aiCreditBundles: (data) => p('dashboard/brand/site/ai/credits/bundles', data, headerJson(), 'post'),
  aiBuyCredits: (data) => p('dashboard/brand/site/ai/credits/buy', data, headerJson(), 'post'),
  listSitePages: (data) => p('dashboard/brand/site/pages/list', data, headerJson(), 'post'),
  createSitePage: (data) => p('dashboard/brand/site/pages/create', data, headerJson(), 'post'),
  getSitePage: (data) => p('dashboard/brand/site/pages/get', data, headerJson(), 'post'),
  updateSitePage: (data) => p('dashboard/brand/site/pages/update', data, headerJson(), 'post'),
  deleteSitePage: (data) => p('dashboard/brand/site/pages/delete', data, headerJson(), 'post'),
  addSiteSection: (data) => p('dashboard/brand/site/sections/add', data, headerJson(), 'post'),
  // The section library: finished sections to drop into a page, as opposed to
  // a whole page template.
  addSiteSectionFromPreset: (data) => p('dashboard/brand/site/sections/add-preset', data, headerJson(), 'post'),
  updateSiteSection: (data) => p('dashboard/brand/site/sections/update', data, headerJson(), 'post'),
  deleteSiteSection: (data) => p('dashboard/brand/site/sections/delete', data, headerJson(), 'post'),
  reorderSiteSections: (data) => p('dashboard/brand/site/sections/reorder', data, headerJson(), 'post'),
  // The editor's whole working copy, in one call — what Save draft sends.
  saveSitePageTree: (data) => p('dashboard/brand/site/page/save-tree', data, headerJson(), 'post'),
  // Support tickets, the owner's side.
  listBrandTickets: (data) => p('dashboard/brand/tickets/list', data, headerJson(), 'post'),
  getBrandTicket: (data) => p('dashboard/brand/tickets/get', data, headerJson(), 'post'),
  replyBrandTicket: (data) => p('dashboard/brand/tickets/reply', data, headerJson(), 'post'),
  setBrandTicketStatus: (data) => p('dashboard/brand/tickets/status', data, headerJson(), 'post'),
  // A ready-made section built as data; nothing is written until Save.
  buildSectionPreset: (data) => p('dashboard/brand/site/section-presets/build', data, headerJson(), 'post'),
  addSiteComponent: (data) => p('dashboard/brand/site/components/add', data, headerJson(), 'post'),
  updateSiteComponent: (data) => p('dashboard/brand/site/components/update', data, headerJson(), 'post'),
  deleteSiteComponent: (data) => p('dashboard/brand/site/components/delete', data, headerJson(), 'post'),
  reorderSiteComponents: (data) => p('dashboard/brand/site/components/reorder', data, headerJson(), 'post'),
  // Site builder asset upload. Caller must pass a FormData with `brand_id`
  // and a single `file` field. Returns `{ url }`.
  uploadSiteAsset: (formData) =>
    p('dashboard/brand/site/assets/upload', formData, headerFormData(), 'post'),

  // ─── Public Site (no auth) ────────────────────────────────────────────────────
  // `entry_slug` is the second segment of a detail address — /blog/my-post.
  // It was not in the signature, so PublicSite passed it and it was dropped
  // on the floor: every detail page asked for the list page instead and the
  // entry was never requested.
  publicGetPage: ({ brand_id, brand, slug, entry_slug, lang } = {}) =>
    new Promise((resolve, reject) => {
      const qs = new URLSearchParams();
      if (brand_id) qs.set('brand_id', brand_id);
      if (brand) qs.set('brand', brand);
      if (slug) qs.set('slug', slug);
      if (entry_slug) qs.set('entry_slug', entry_slug);
      if (lang) qs.set('lang', lang);
      const url = `public/site/page${qs.toString() ? `?${qs.toString()}` : ''}`;
      fetchAPIFrom(url, { cache: 'no-cache' }, null, 'get').then(resolve).catch(reject);
    }),
  // The brand's header/footer with no page — keeps a not-found screen inside
  // the brand's own site instead of showing a bare error page.
  publicSiteFrame: ({ brand_id, brand } = {}) =>
    new Promise((resolve, reject) => {
      const qs = new URLSearchParams();
      if (brand_id) qs.set('brand_id', brand_id);
      if (brand) qs.set('brand', brand);
      const url = `public/site/frame${qs.toString() ? `?${qs.toString()}` : ''}`;
      fetchAPIFrom(url, { cache: 'no-cache' }, null, 'get').then(resolve).catch(reject);
    }),
  publicListPages: ({ brand_id, brand } = {}) =>
    new Promise((resolve, reject) => {
      const qs = new URLSearchParams();
      if (brand_id) qs.set('brand_id', brand_id);
      if (brand) qs.set('brand', brand);
      const url = `public/site/pages${qs.toString() ? `?${qs.toString()}` : ''}`;
      fetchAPIFrom(url, { cache: 'no-cache' }, null, 'get').then(resolve).catch(reject);
    }),
  // Asks the backend what to render for the current Host header.
  // Resolves to { brand, route: { type, subdomain, page? } } on a brand
  // domain, or rejects with a 404 on the platform host (which the
  // dispatcher treats as "show the platform landing").
  publicResolvedRoute: () =>
    new Promise((resolve, reject) => {
      fetchAPIFrom('public/site/route', { cache: 'no-cache' }, null, 'get')
        .then(resolve).catch(reject);
    }),

  // ─── Platform Admin (single super-admin, parham.firou@techproone.com) ──────────────
  adminSummary:       (data) => p('dashboard/admin/summary',       data, headerJson(), 'post'),
  adminHealth:        (data) => p('dashboard/admin/health',        data, headerJson(), 'post'),
  adminStorage:       (data) => p('dashboard/admin/storage',       data, headerJson(), 'post'),
  adminListUsers:     (data) => p('dashboard/admin/users',         data, headerJson(), 'post'),
  adminListBrands:    (data) => p('dashboard/admin/brands',        data, headerJson(), 'post'),
  adminListDomains:   (data) => p('dashboard/admin/domains',       data, headerJson(), 'post'),
  adminListSubs:      (data) => p('dashboard/admin/subscriptions', data, headerJson(), 'post'),
  adminEarnings:      (data) => p('dashboard/admin/earnings',      data, headerJson(), 'post'),
  adminListErrors:    (data) => p('dashboard/admin/errors',        data, headerJson(), 'post'),
  adminSetUserActive:  (data) => p('dashboard/admin/users/set-active',     data, headerJson(), 'post'),
  adminSendReset:      (data) => p('dashboard/admin/users/send-reset',     data, headerJson(), 'post'),
  adminUserDetails:    (data) => p('dashboard/admin/users/details',        data, headerJson(), 'post'),
  adminBrandDetails:   (data) => p('dashboard/admin/brands/details',       data, headerJson(), 'post'),
  adminSetBrandActive: (data) => p('dashboard/admin/brands/set-active',    data, headerJson(), 'post'),
  adminCancelSub:      (data) => p('dashboard/admin/subscriptions/cancel', data, headerJson(), 'post'),

  // Subscription plans — the Stripe products/prices brands subscribe to.
  adminListPlans:       (data) => p('dashboard/admin/plans', data, headerJson(), 'post'),
  adminCreatePlan:      (data) => p('dashboard/admin/plans/create', data, headerJson(), 'post'),
  adminUpdatePlan:      (data) => p('dashboard/admin/plans/update', data, headerJson(), 'post'),
  adminChangePlanPrice: (data) => p('dashboard/admin/plans/price', data, headerJson(), 'post'),
  adminSetPlanActive:   (data) => p('dashboard/admin/plans/active', data, headerJson(), 'post'),
  adminGrantAiCredits:  (data) => p('dashboard/admin/ai/credits', data, headerJson(), 'post'),
  adminResumeSub:      (data) => p('dashboard/admin/subscriptions/resume', data, headerJson(), 'post'),
  adminRefundLatestSub: (data) => p('dashboard/admin/subscriptions/refund-latest', data, headerJson(), 'post'),
  adminListAudit:      (data) => p('dashboard/admin/audit',                  data, headerJson(), 'post'),

  // CSV exports. The server returns text/csv so we bypass `p()` (which
  // expects JSON) and hand back the raw blob URL the caller can pipe
  // into a hidden <a download>.
  adminExportUsers:    (data) => adminCsvFetch('dashboard/admin/users/export',   data),
  adminExportBrands:   (data) => adminCsvFetch('dashboard/admin/brands/export',  data),
  adminExportDomains:  (data) => adminCsvFetch('dashboard/admin/domains/export', data),
  adminExportAudit:    (data) => adminCsvFetch('dashboard/admin/audit/export',   data),

  // ─── Company Billing ──────────────────────────────────────────────────────────
  getBillingDetails: (data) => p('dashboard/brand/settings/biilling/billingDetails', data, headerJson(), 'post'),
  saveCard: (data) => p('dashboard/brand/settings/biilling/cardSave', data, headerJson(), 'post'),
  getCardList: (data) => p('dashboard/brand/settings/biilling/cardList', data, headerJson(), 'post'),

  // ─── Stripe Payments ──────────────────────────────────────────────────────────

  // ─── User Contact (per-user saved contacts) ───────────────────────────────────
  saveUserContact: (data) => p('subscribe/contact/saveAndUpdate', data, headerJson(), 'post'),
  getUserContact: () =>
    new Promise((resolve, reject) =>
      fetchAPIFrom('subscribe/contact/findByUserId', { cache: 'no-cache', headers: headerJson() }, null, 'get')
        .then(resolve).catch(reject)
    ),
  deleteUserContact: (data) => p('subscribe/contact/deleteById', data, headerJson(), 'delete'),

  // ─── Public ───────────────────────────────────────────────────────────────────
  sendContracUs: (data) => p('contactus', data, { 'Content-Type': 'application/json' }, 'post'),
  subscribe: (data) => p('subscribe', data, { 'Content-Type': 'application/json' }, 'post'),

  // ─── Wallet ───────────────────────────────────────────────────────────────────
  verifyWallet: (data) => p('verify', data, { 'Content-Type': 'application/json' }, 'post'),
};
